import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Mail, ArrowLeft, FileText, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ConfirmationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { subscription } = location.state || {};
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (!subscription) {
      navigate('/');
    }
  }, [subscription, navigate]);

  const handleDownloadContract = async () => {
    if (!subscription?.id) return;
    
    setIsDownloading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/subscriptions/${subscription.id}/contract`);
      
      if (!response.ok) {
        throw new Error('Kon contract niet downloaden');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contract-${subscription.company_name.replace(/\s+/g, '-')}-${subscription.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Contract gedownload!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Er ging iets mis bij het downloaden van het contract');
    } finally {
      setIsDownloading(false);
    }
  };

  if (!subscription) {
    return null;
  }

  const packageNames = {
    starter: 'Starter',
    professional: 'Professional',
    whitelabel: 'Premium White-label'
  };

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="border-2 border-[#C9A227]/30 bg-white shadow-xl" data-testid="confirmation-card">
          <CardHeader className="text-center pb-6">
            <div className="w-16 h-16 bg-green-100 border-2 border-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl md:text-3xl font-bold text-gray-900">
              Aanmelding Ontvangen!
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Uw aanmelding</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Bedrijf</span>
                  <span className="font-medium text-gray-900">{subscription.company_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Pakket</span>
                  <span className="font-medium text-[#C9A227]">{packageNames[subscription.package]}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                    In behandeling
                  </span>
                </div>
              </div>
            </div>

            {/* Download Contract Section */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-100 border border-blue-300 rounded-full flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-2">Uw Contract</h4>
                  <p className="text-gray-600 text-sm mb-4">
                    Download uw ondertekende overeenkomst als PDF voor uw administratie.
                  </p>
                  <Button 
                    onClick={handleDownloadContract}
                    disabled={isDownloading}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="download-contract-btn"
                  >
                    {isDownloading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Downloaden...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Download Contract (PDF)
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            <div className="bg-[#C9A227]/5 border-2 border-[#C9A227] rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#C9A227]/10 border border-[#C9A227] rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#C9A227]" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Wat gebeurt er nu?</h4>
                  <ul className="text-gray-600 space-y-2 text-sm">
                    <li>• U ontvangt binnen 24 uur een bevestigingsmail</li>
                    <li>• Wij nemen contact met u op voor de oplevering</li>
                    <li>• Facturatie start pas na oplevering van de app</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="pt-4">
              <Button 
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full py-6 border-2 border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227] hover:text-white"
                data-testid="back-to-home-btn"
              >
                <ArrowLeft className="mr-2 w-5 h-5" /> Terug naar Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
